import React, { useState } from 'react';
import { BACKEND_URL } from '../config';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { 
  Send, 
  Building2, 
  MapPin,
  Handshake,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = BACKEND_URL;

const collaborationTypes = [
  { value: 'partnership', label: 'Partnership', description: 'General strategic partnership' },
  { value: 'knowledge_sharing', label: 'Knowledge Sharing', description: 'Exchange expertise and best practices' },
  { value: 'joint_project', label: 'Joint Project', description: 'Collaborate on a specific project' },
  { value: 'resource_sharing', label: 'Resource Sharing', description: 'Share resources, facilities, or equipment' },
  { value: 'mentorship', label: 'Mentorship', description: 'Guidance and support relationship' },
  { value: 'other', label: 'Other', description: 'Other type of collaboration' },
];

const CollaborationRequestDialog = ({ 
  open, 
  onOpenChange, 
  targetOrganization,
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    collaboration_type: '',
    title: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.collaboration_type || !formData.title || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/collaboration-requests`, {
        receiver_actor_id: targetOrganization.id,
        collaboration_type: formData.collaboration_type,
        title: formData.title,
        message: formData.message
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      toast.success(`Collaboration request sent to ${targetOrganization.organization_name}!`);
      setFormData({ collaboration_type: '', title: '', message: '' });
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Failed to send collaboration request:', error);
      const message = error.response?.data?.detail || 'Failed to send collaboration request';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const selectedType = collaborationTypes.find(t => t.value === formData.collaboration_type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Handshake className="h-5 w-5 text-blue-600" />
            Request Collaboration
          </DialogTitle>
        </DialogHeader>

        {targetOrganization && (
          <div className="bg-gray-50 rounded-lg p-3 mb-4">
            <div className="flex items-start gap-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Building2 className="h-5 w-5 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-medium text-sm truncate">
                  {targetOrganization.organization_name}
                </h4>
                <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                  <MapPin className="h-3 w-3" />
                  {targetOrganization.city}, {targetOrganization.country}
                </div>
                {targetOrganization.distance_km && (
                  <Badge variant="outline" className="text-xs mt-1">
                    {targetOrganization.distance_km} km away
                  </Badge>
                )}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="collaboration_type">Collaboration Type *</Label>
            <Select
              value={formData.collaboration_type}
              onValueChange={(value) => setFormData(prev => ({ ...prev, collaboration_type: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select collaboration type" />
              </SelectTrigger>
              <SelectContent>
                {collaborationTypes.map(type => (
                  <SelectItem key={type.value} value={type.value}>
                    <div>
                      <div className="font-medium">{type.label}</div>
                      <div className="text-xs text-gray-500">{type.description}</div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedType && (
              <p className="text-xs text-gray-500">{selectedType.description}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="title">Request Title *</Label>
            <Input
              id="title"
              placeholder="e.g., Partnership Proposal for Community Initiative"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              maxLength={100}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message *</Label>
            <Textarea
              id="message"
              placeholder="Describe why you'd like to collaborate and what you hope to achieve together..."
              value={formData.message}
              onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
              rows={5}
              maxLength={1000}
            />
            <p className="text-xs text-gray-500 text-right">
              {formData.message.length}/1000 characters
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Request
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CollaborationRequestDialog;
